import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, FileText, Calendar, Mail, DollarSign, LogOut, Building2, UserCircle, Images, HandHeart } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    programs: 0,
    news: 0,
    events: 0,
    submissions: 0,
    team: 0
  });
  const navigate = useNavigate();

  const fetchStats = async (token) => {
    try {
      const [programsRes, newsRes, eventsRes, contactRes, volunteerRes, teamRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/programs`),
        axios.get(`${BACKEND_URL}/api/news?published_only=false`),
        axios.get(`${BACKEND_URL}/api/events?active_only=false`),
        axios.get(`${BACKEND_URL}/api/submissions/contact`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/submissions/volunteer`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/team?active_only=false`)
      ]);
      
      setStats({
        programs: programsRes.data.length,
        news: newsRes.data.length,
        events: eventsRes.data.length,
        submissions: contactRes.data.length + volunteerRes.data.length,
        team: teamRes.data.length
      });
    } catch (error) {
      // Stats fetch failed - non-critical, continue with defaults
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');

    if (!token || !userData) {
      navigate('/admin/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchStats(token);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (!user) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/images/gateway-logo.png" 
                alt="Gateway Inc. Logo" 
                className="h-12 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#0A345E]">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Content Management System</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user.full_name}!</h2>
          <p className="text-gray-600">Manage your website content from this dashboard</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="border-l-4 border-l-[#007A7A]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Programs</CardTitle>
              <Users className="h-5 w-5 text-[#007A7A]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.programs}</div>
              <p className="text-xs text-gray-600">Active programs</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-600">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">News Articles</CardTitle>
              <FileText className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.news}</div>
              <p className="text-xs text-gray-600">Total articles</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-600">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Events</CardTitle>
              <Calendar className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.events}</div>
              <p className="text-xs text-gray-600">Scheduled events</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-600">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Submissions</CardTitle>
              <Mail className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.submissions}</div>
              <p className="text-xs text-gray-600">Total forms</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-600">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Team</CardTitle>
              <UserCircle className="h-5 w-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.team}</div>
              <p className="text-xs text-gray-600">Team members</p>
            </CardContent>
          </Card>
        </div>

        {/* Management Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/programs')}
            data-testid="manage-programs-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Users className="w-5 h-5 text-[#007A7A]" />
                Manage Programs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Add, edit, or remove program information</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Manage Programs</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/news')}
            data-testid="manage-news-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                News & Blog
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Create and publish news articles</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Manage News</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/team')}
            data-testid="manage-team-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-orange-600" />
                Team Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Update team information and photos</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Manage Team</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/events')}
            data-testid="manage-events-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Schedule and manage upcoming events</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Manage Events</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/submissions')}
            data-testid="manage-submissions-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Form Submissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">View contact and volunteer forms</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">View Submissions</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/donations')}
            data-testid="manage-donations-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Donations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Track donation records and stats</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">View Donations</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/gallery')}
            data-testid="manage-gallery-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Images className="w-5 h-5 text-pink-600" />
                Photo Gallery
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Upload and organize event photos</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Manage Gallery</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/organization')}
            data-testid="manage-organization-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0A345E]" />
                Organization Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Update contact info and mission</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Edit Settings</Button>
            </CardContent>
          </Card>

          <Card 
            className="hover:shadow-lg transition-shadow duration-300 cursor-pointer" 
            onClick={() => navigate('/admin/volunteer-opportunities')}
            data-testid="manage-volunteer-opportunities-card"
          >
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <HandHeart className="w-5 h-5 text-[#85955D]" />
                Volunteer Opportunities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">Manage volunteer roles and positions</p>
              <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90">Manage Opportunities</Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;